import * as React from "react";

import { cn } from "@/lib/utils";

interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
  /**
   * Disable the default sticky first/second column behavior. Use for tables
   * that don't have a checkbox + ID column pattern.
   */
  noStickyColumns?: boolean;
  /**
   * Number of leading columns to freeze when horizontally scrolling.
   * Defaults to 2 (checkbox + ID). Set to 3 for tables with an extra
   * leading column (e.g. checkbox + star + ID). Ignored when
   * `noStickyColumns` is true.
   */
  stickyColumns?: 2 | 3;
}

const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ className, noStickyColumns, stickyColumns = 2, ...props }, ref) => (
    <div className="relative w-full overflow-auto bg-background rounded-md">
      <table
        ref={ref}
        className={cn(
          "w-full caption-bottom text-sm",
          !noStickyColumns && [
            // Sticky first column (checkbox) – narrow, no right padding
            "[&_thead_th:first-child]:sticky [&_thead_th:first-child]:left-0 [&_thead_th:first-child]:z-20 [&_thead_th:first-child]:bg-background [&_thead_th:first-child]:w-10 [&_thead_th:first-child]:px-3",
            "[&_tbody_td:first-child]:sticky [&_tbody_td:first-child]:left-0 [&_tbody_td:first-child]:z-10 [&_tbody_td:first-child]:bg-inherit [&_tbody_td:first-child]:w-10 [&_tbody_td:first-child]:px-3",
            "[&_tfoot_td:first-child]:sticky [&_tfoot_td:first-child]:left-0 [&_tfoot_td:first-child]:z-10 [&_tfoot_td:first-child]:bg-background [&_tfoot_td:first-child]:w-10 [&_tfoot_td:first-child]:px-3",
            // Sticky second column – offset matches first column width.
            // When stickyColumns === 3, this is a narrow extra column (e.g. star)
            // and gets no shadow; otherwise it's the ID column with shadow.
            stickyColumns === 3
              ? [
                  "[&_thead_th:nth-child(2)]:sticky [&_thead_th:nth-child(2)]:left-[40px] [&_thead_th:nth-child(2)]:z-20 [&_thead_th:nth-child(2)]:bg-background [&_thead_th:nth-child(2)]:w-10 [&_thead_th:nth-child(2)]:px-3",
                  "[&_tbody_td:nth-child(2)]:sticky [&_tbody_td:nth-child(2)]:left-[40px] [&_tbody_td:nth-child(2)]:z-10 [&_tbody_td:nth-child(2)]:bg-inherit [&_tbody_td:nth-child(2)]:w-10 [&_tbody_td:nth-child(2)]:px-3",
                  "[&_tfoot_td:nth-child(2)]:sticky [&_tfoot_td:nth-child(2)]:left-[40px] [&_tfoot_td:nth-child(2)]:z-10 [&_tfoot_td:nth-child(2)]:bg-background [&_tfoot_td:nth-child(2)]:w-10 [&_tfoot_td:nth-child(2)]:px-3",
                  // Sticky third column (ID) – offset = 40 + 40 = 80px
                  "[&_thead_th:nth-child(3)]:sticky [&_thead_th:nth-child(3)]:left-[80px] [&_thead_th:nth-child(3)]:z-20 [&_thead_th:nth-child(3)]:bg-background [&_thead_th:nth-child(3)]:shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]",
                  "[&_tbody_td:nth-child(3)]:sticky [&_tbody_td:nth-child(3)]:left-[80px] [&_tbody_td:nth-child(3)]:z-10 [&_tbody_td:nth-child(3)]:bg-inherit [&_tbody_td:nth-child(3)]:shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]",
                  "[&_tfoot_td:nth-child(3)]:sticky [&_tfoot_td:nth-child(3)]:left-[80px] [&_tfoot_td:nth-child(3)]:z-10 [&_tfoot_td:nth-child(3)]:bg-background [&_tfoot_td:nth-child(3)]:shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]",
                ]
              : [
                  "[&_thead_th:nth-child(2)]:sticky [&_thead_th:nth-child(2)]:left-[40px] [&_thead_th:nth-child(2)]:z-20 [&_thead_th:nth-child(2)]:bg-background [&_thead_th:nth-child(2)]:shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]",
                  "[&_tbody_td:nth-child(2)]:sticky [&_tbody_td:nth-child(2)]:left-[40px] [&_tbody_td:nth-child(2)]:z-10 [&_tbody_td:nth-child(2)]:bg-inherit [&_tbody_td:nth-child(2)]:shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]",
                  "[&_tfoot_td:nth-child(2)]:sticky [&_tfoot_td:nth-child(2)]:left-[40px] [&_tfoot_td:nth-child(2)]:z-10 [&_tfoot_td:nth-child(2)]:bg-background [&_tfoot_td:nth-child(2)]:shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]",
                ],
          ],
          className,
        )}
        {...props}
      />
    </div>
  ),
);
Table.displayName = "Table";

const TableHeader = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <thead
      ref={ref}
      className={cn(
        "[&_tr]:border-b bg-background",
        // Header rows never react to hover or selection — only body rows do.
        // Use !important to defeat the hover/selected utilities applied by TableRow.
        "[&_tr]:!cursor-default [&_tr:hover]:!bg-background",
        "[&_tr:hover>th]:!bg-background [&_tr:hover>td]:!bg-background",
        "[&_tr[data-state=selected]]:!bg-background [&_tr[data-state=selected]]:!shadow-none",
        "[&_tr[data-state=selected]>th]:!bg-background [&_tr[data-state=selected]>td]:!bg-background",
        className,
      )}
      {...props}
    />
  ),
);
TableHeader.displayName = "TableHeader";

const TableBody = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <tbody ref={ref} className={cn("[&_tr:last-child]:border-0", className)} {...props} />
  ),
);
TableBody.displayName = "TableBody";

const TableFooter = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <tfoot ref={ref} className={cn("border-t bg-muted/50 font-medium [&>tr]:last:border-b-0", className)} {...props} />
  ),
);
TableFooter.displayName = "TableFooter";

interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  /** Mark the row as the currently selected/active row (sets data-state="selected"). */
  selected?: boolean;
}

const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, selected, ...props }, ref) => {
    const interactive = selected !== undefined || typeof props.onClick === "function";
    return (
      <tr
        ref={ref}
        data-state={selected ? "selected" : props["data-state"]}
        className={cn(
          "group/row border-b transition-colors bg-background",
          interactive && "cursor-pointer",
          // Hover state — visible in both light and dark
          "hover:bg-row-hover",
          // Selected state — action tint + left primary accent, frozen cells follow
          "data-[state=selected]:bg-row-selected data-[state=selected]:shadow-[inset_2px_0_0_0_hsl(var(--primary))]",
          className,
        )}
        {...props}
      />
    );
  },
);
TableRow.displayName = "TableRow";

const TableHead = React.forwardRef<HTMLTableCellElement, React.ThHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        "h-12 px-4 text-left align-middle font-medium text-muted-foreground whitespace-nowrap [&:has([role=checkbox])]:pr-0",
        className,
      )}
      {...props}
    />
  ),
);
TableHead.displayName = "TableHead";

const TableCell = React.forwardRef<HTMLTableCellElement, React.TdHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <td ref={ref} className={cn("bg-inherit p-4 align-middle [&:has([role=checkbox])]:pr-0", className)} {...props} />
  ),
);
TableCell.displayName = "TableCell";

const TableCaption = React.forwardRef<HTMLTableCaptionElement, React.HTMLAttributes<HTMLTableCaptionElement>>(
  ({ className, ...props }, ref) => (
    <caption ref={ref} className={cn("mt-4 text-sm text-muted-foreground", className)} {...props} />
  ),
);
TableCaption.displayName = "TableCaption";

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };
